import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as userMessageService from '../../services/userMessageService';
import socketService from '../../services/socketService';
import { toast } from 'react-toastify';

export type SidebarItem = {
  href: string;
  icon: React.ReactNode;
  text: string;
  badge?: string;
  tabs?: {
    href: string;
    text: string;
  }[];
};

interface UserSidebarProps {
  userData: any;
}

const UserSidebar: React.FC<UserSidebarProps> = ({ userData }) => {
  const [unreadCount, setUnreadCount] = useState<number>(0);

  // Fetch unread messages count and set up real-time updates
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await userMessageService.getUnreadMessagesCount();
        setUnreadCount(response.count);
      } catch (error) {

      }
    };

    if (userData) {
      // Initial fetch
      fetchUnreadCount();
      
      // Set up socket listener for real-time updates
      socketService.setOnUnreadCountUpdate((count: number) => {
        setUnreadCount(count);
      });
      
      // Set up interval as fallback (reduced frequency since we have real-time updates)
      const interval = setInterval(fetchUnreadCount, 60000); // Every minute as fallback
      
      return () => {
        clearInterval(interval);
        // Clear socket listeners
        socketService.setOnUnreadCountUpdate(() => {});
      };
    }
  }, [userData]);
  // Regular user menu items
  const sidebarItems: SidebarItem[] = [
    {
      href: "/home",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 576 512" fill="currentColor">
          <path d="M575.8 255.5c0 18-15 32.1-32 32.1h-32l.7 160.2c0 2.7-.2 5.4-.5 8.1V472c0 22.1-17.9 40-40 40H456c-1.1 0-2.2 0-3.3-.1c-1.4 .1-2.8 .1-4.2 .1H416 392c-22.1 0-40-17.9-40-40V448 384c0-17.7-14.3-32-32-32H256c-17.7 0-32 14.3-32 32v64 24c0 22.1-17.9 40-40 40H160 128.1c-1.5 0-3-.1-4.5-.2c-1.2 .1-2.4 .2-3.6 .2H104c-22.1 0-40-17.9-40-40V360c0-.9 0-1.9 .1-2.8V287.6H32c-18 0-32-14-32-32.1c0-9 3-17 10-24L266.4 8c7-7 15-8 22-8s15 2 21 7L564.8 231.5c8 7 12 15 11 24z" />
        </svg>
      ),
      text: "خانه"
    },
    {
      href: "/new-test-request",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 448 512" fill="currentColor">
          <path d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32V224H48c-17.7 0-32 14.3-32 32s14.3 32 32 32H192V432c0 17.7 14.3 32 32 32s32-14.3 32-32V288H400c17.7 0 32-14.3 32-32s-14.3-32-32-32H256V80z" />
        </svg>
      ),
      text: "درخواست آزمایش جدید"
    },
    {
      href: "/my-tests",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 512 512" fill="currentColor">
          <path d="M471.6 21.7c-21.9-21.9-57.3-21.9-79.2 0L362.3 51.7l97.9 97.9 30.1-30.1c21.9-21.9 21.9-57.3 0-79.2L471.6 21.7zm-299.2 220c-6.1 6.1-10.8 13.6-13.5 21.9l-29.6 88.8c-2.9 8.6-.6 18.1 5.8 24.6s15.9 8.7 24.6 5.8l88.8-29.6c8.2-2.7 15.7-7.4 21.9-13.5L437.7 172.3 339.7 74.3 172.4 241.7zM96 64C43 64 0 107 0 160V416c0 53 43 96 96 96H352c53 0 96-43 96-96V320c0-17.7-14.3-32-32-32s-32 14.3-32 32v96c0 17.7-14.3 32-32 32H96c-17.7 0-32-14.3-32-32V160c0-17.7 14.3-32 32-32h96c17.7 0 32-14.3 32-32s-14.3-32-32-32H96z" />
        </svg>
      ),
      text: "آزمایش‌های من"
    },
    {
      href: "/receive-image-report",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 576 512" fill="currentColor">
          <path d="M64 64C28.7 64 0 92.7 0 128V384c0 35.3 28.7 64 64 64H512c35.3 0 64-28.7 64-64V128c0-35.3-28.7-64-64-64H64zm64 320H64V320c35.3 0 64 28.7 64 64zM64 192V128h64c0 35.3-28.7 64-64 64zM448 384c0-35.3 28.7-64 64-64v64H448zm64-192c-35.3 0-64-28.7-64-64h64v64zM288 160a96 96 0 1 1 0 192 96 96 0 1 1 0-192z" />
        </svg>
      ),
      text: "دریافت تصویر جواب آزمایش"
    },
    {
      href: "/view-online-results",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 576 512" fill="currentColor">
          <path d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-92.7-69.4c-6.2 0-11.4 5.2-11.4 11.4s5.2 11.4 11.4 11.4c28.4 0 51.4 23 51.4 51.4c0 28.4-23 51.4-51.4 51.4z"/>
        </svg>
      ),
      text: "مشاهده آنلاین جواب"
    },
    // Temporarily disabled - under maintenance
    // {
    //   href: "/tickets",
    //   icon: (
    //     <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 512 512" fill="currentColor">
    //       <path d="M471.6 21.7c-21.9-21.9-57.3-21.9-79.2 0L362.3 51.7l97.9 97.9 30.1-30.1c21.9-21.9 21.9-57.3 0-79.2L471.6 21.7zm-299.2 220c-6.1 6.1-10.8 13.6-13.5 21.9l-29.6 88.8c-2.9 8.6-.6 18.1 5.8 24.6s15.9 8.7 24.6 5.8l88.8-29.6c8.2-2.7 15.7-7.4 21.9-13.5L437.7 172.3 339.7 74.3 172.4 241.7zM96 64C43 64 0 107 0 160V416c0 53 43 96 96 96H352c53 0 96-43 96-96V320c0-17.7-14.3-32-32-32s-32 14.3-32 32v96c0 17.7-14.3 32-32 32H96c-17.7 0-32-14.3-32-32V160c0-17.7 14.3-32 32-32h96c17.7 0 32-14.3 32-32s-14.3-32-32-32H96z"/>
    //     </svg>
    //   ),
    //   text: "تیکت ها"
    // },
    {
      href: "/bank-payment",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 576 512" fill="currentColor">
          <path d="M64 64C28.7 64 0 92.7 0 128V384c0 35.3 28.7 64 64 64H512c35.3 0 64-28.7 64-64V128c0-35.3-28.7-64-64-64H64zm64 320H64V320c35.3 0 64 28.7 64 64zM64 192V128h64c0 35.3-28.7 64-64 64zM448 384c0-35.3 28.7-64 64-64v64H448zm64-192c-35.3 0-64-28.7-64-64h64v64zM288 160a96 96 0 1 1 0 192 96 96 0 1 1 0-192z" />
        </svg>
      ),
      text: "پرداخت باقیمانده "
    },
    {
      href: "/profile",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 448 512" fill="currentColor">
          <path d="M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512H418.3c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304H178.3z" />
        </svg>
      ),
      text: "پروفایل من"
    },
    {
      href: "/messages",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 512 512" fill="currentColor">
          <path d="M64 0C28.7 0 0 28.7 0 64V352c0 35.3 28.7 64 64 64h96v80c0 6.1 3.4 11.6 8.8 14.3s11.9 2.1 16.8-1.5L309.3 416H448c35.3 0 64-28.7 64-64V64c0-35.3-28.7-64-64-64H64z"/>
        </svg>
      ),
      text: "پیامهای سیستم",
      badge: unreadCount > 0 ? unreadCount.toString() : undefined
    },
    {
      href: "/help",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 512 512" fill="currentColor">
          <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM169.8 165.3c7.9-22.3 29.1-37.3 52.8-37.3h58.3c34.9 0 63.1 28.3 63.1 63.1c0 22.6-12.1 43.5-31.7 54.8L280 264.4c-.2 13-10.9 23.6-24 23.6c-13.3 0-24-10.7-24-24V250.5c0-8.6 4.6-16.5 12.1-20.8l44.3-25.4c4.7-2.7 7.6-7.7 7.6-13.1c0-8.4-6.8-15.1-15.1-15.1H222.6c-3.4 0-6.4 2.1-7.5 5.3l-.4 1.2c-4.4 12.5-18.2 19-30.6 14.6s-19-18.2-14.6-30.6l .4-1.2zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z" />
        </svg>
      ),
      text: "راهنما"
    }
  ];

  return (
    <div className="nav-item-wrapper flex flex-col gap-2">
      {sidebarItems.map((item, index) => (
        <React.Fragment key={index}>
          <Link 
            to={item.href} 
            className="flex items-center gap-2 hover:bg-slate-50 px-2 py-2 rounded-sm h-10"
            onClick={item.href === '/new-test-request' ? () => toast.warning('شما در حال ثبت درخواست جدید هستید') : undefined}
          >
            <div className="nav-item-icon text-slate-800">
              {item.icon}
            </div>
            <div className="nav-icon-text text-sm text-slate-800 font-medium">
              {item.text}
            </div>
            {item.badge && (
              <span className="bg-indigo-50 text-xs text-indigo-500 py-0.5 px-2 rounded-sm border-1 border-indigo-500">
                {item.badge}
              </span>
            )}
          </Link>
          {item.tabs && item.tabs.length > 0 && (
            <div className="ml-8 flex flex-col gap-1 mt-1 mb-2">
              {item.tabs.map((tab, tabIndex) => (
                <Link
                  key={`${index}-${tabIndex}`}
                  to={tab.href}
                  className="flex items-center gap-2 hover:bg-slate-50 px-2 py-1.5 rounded-sm text-slate-600"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-400"></div>
                  <div className="text-xs font-medium">{tab.text}</div>
                </Link>
              ))}
            </div>
          )}
        </React.Fragment>
      ))}  
    </div>
  );
};

export default UserSidebar;
